import React, { useEffect, useRef, useState } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { useLocation } from 'react-router-dom';
import { parseDateToIso } from './config';

export function SearchableSelect({
    value,
    options,
    onChange,
    placeholder,
    disabled,
    required,
    allowClear
}) {
    const wrapperRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(-1);

    const safeOptions = Array.isArray(options) ? options : [];
    const selected = safeOptions.find(o => String(o.value) === String(value));

    useEffect(() => {
        if (!open) setQuery(selected ? String(selected.label || '') : '');
    }, [open, selected]);

    useEffect(() => {
        const handler = (e) => {
            const el = wrapperRef.current;
            if (el && !el.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, []);

    const normalizedQuery = String(query || '').trim().toLowerCase();
    const filtered = normalizedQuery
        ? safeOptions.filter(o => String(o.label || '').toLowerCase().includes(normalizedQuery))
        : safeOptions;

    const commit = (opt) => {
        if (!opt) return;
        setQuery(String(opt.label || ''));
        setOpen(false);
        setHighlightIndex(-1);
        if (typeof onChange === 'function') onChange(String(opt.value), opt);
    };

    const handleKeyDown = (e) => {
        if (disabled) return;
        if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            setOpen(true);
            return;
        }
        if (!open) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(i => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const target = filtered[Math.max(0, highlightIndex)];
            if (target) commit(target);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="position-relative">
            <input
                type="text"
                className="form-control"
                value={query}
                placeholder={placeholder}
                disabled={!!disabled}
                required={!!required}
                onChange={(e) => {
                    const next = e.target.value;
                    setQuery(next);
                    setOpen(true);
                    setHighlightIndex(0);
                }}
                onFocus={() => {
                    if (disabled) return;
                    setOpen(true);
                }}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                style={allowClear ? { paddingRight: 46 } : undefined}
            />

            {allowClear && value ? (
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm position-absolute top-50 end-0 translate-middle-y me-1"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        setQuery('');
                        setOpen(false);
                        setHighlightIndex(-1);
                        if (typeof onChange === 'function') onChange('', null);
                    }}
                    disabled={!!disabled}
                >
                    Clear
                </button>
            ) : null}

            {open && !disabled ? (
                <div
                    className="list-group position-absolute w-100 shadow"
                    style={{ maxHeight: 260, overflowY: 'auto', zIndex: 1050 }}
                >
                    {filtered.length === 0 ? (
                        <div className="list-group-item text-muted">No results</div>
                    ) : (
                        filtered.map((opt, idx) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`list-group-item list-group-item-action ${idx === highlightIndex ? 'active' : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onTouchStart={(e) => e.preventDefault()}
                                onClick={() => commit(opt)}
                            >
                                {opt.label}
                            </button>
                        ))
                    )}
                </div>
            ) : null}
        </div>
    );
}

export function DateInput({ value, onChange, placeholder, disabled, required }) {
    const dateRef = useRef(null);

    const formatDdMmYyyy = (raw) => {
        const s = String(raw || "").trim();
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return "";
        return `${m[3]}/${m[2]}/${m[1]}`;
    };

    const [text, setText] = useState(formatDdMmYyyy(value));

    useEffect(() => {
        setText(formatDdMmYyyy(value));
    }, [value]);

    return (
        <div className="input-group">
            <input
                type="text"
                className="form-control"
                value={text}
                placeholder={placeholder || "dd/mm/yyyy"}
                disabled={!!disabled}
                required={!!required}
                onChange={(e) => {
                    const nextText = String(e.target.value || "");
                    setText(nextText);
                    if (typeof onChange !== "function") return;
                    const iso = parseDateToIso(nextText);
                    if (iso) onChange(iso);
                }}
                onBlur={() => {
                    const iso = parseDateToIso(text);
                    setText(formatDdMmYyyy(iso));
                }}
                inputMode="numeric"
            />
            <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={!!disabled}
                onClick={() => {
                    const el = dateRef.current;
                    if (!el) return;
                    if (typeof el.showPicker === "function") return el.showPicker();
                    el.click();
                }}
                tabIndex={-1}
            >
                📅
            </button>
            <input
                ref={dateRef}
                type="date"
                value={value ? String(value).slice(0, 10) : ""}
                disabled={!!disabled}
                onChange={(e) => {
                    const next = String(e.target.value || "").trim();
                    if (typeof onChange === "function") onChange(next);
                    setText(formatDdMmYyyy(next));
                }}
                style={{ position: "absolute", opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
                tabIndex={-1}
            />
        </div>
    );
}

export function usePagination(items, pageSize = 10) {
    const safeItems = Array.isArray(items) ? items : [];
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(safeItems.length / pageSize));

    useEffect(() => {
        setCurrentPage(1);
    }, [safeItems, pageSize]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const startIndex = safeItems.length === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
    const endIndex = safeItems.length === 0 ? 0 : Math.min(currentPage * pageSize, safeItems.length);
    const pagedItems = safeItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return {
        currentPage,
        setCurrentPage,
        totalPages,
        pagedItems,
        totalItems: safeItems.length,
        startIndex,
        endIndex,
        pageSize
    };
}

export function TablePagination({
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    onPageChange
}) {
    if (!totalItems || totalPages <= 1) return null;

    return (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3 no-print">
            <div className="text-muted small">
                Showing {startIndex}-{endIndex} of {totalItems} rows
            </div>
            <div className="btn-group">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage <= 1}
                >
                    Previous
                </button>
                <button type="button" className="btn btn-outline-primary btn-sm" disabled>
                    Page {currentPage} / {totalPages}
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage >= totalPages}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

function Layout({ children }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();

    const toggleSidebar = () => {
        setSidebarOpen(prev => !prev);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    useEffect(() => {
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    }, [location.pathname]);

    return (
        <div className="d-flex flex-column vh-100 overflow-hidden">
            <Navbar toggleSidebar={toggleSidebar} />
            <div className="d-flex flex-grow-1 overflow-hidden position-relative">
                <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} closeSidebar={closeSidebar} />
                <main className="flex-grow-1 p-3 overflow-auto transition-all">
                    {children}
                </main>
                {/* Overlay for mobile when sidebar is open */}
                {sidebarOpen && (
                    <div 
                        className="overlay d-md-none" 
                        onClick={closeSidebar}
                    ></div>
                )}
            </div>
        </div>
    );
}

export default Layout;
